import React, { useState, useEffect } from 'react';
import { IconStar } from '../components/Icons';

// Client testimonials as published on the previous version of this page.
const testimonialsData = [
  {
    tag: 'Client since 2015',
    tagTone: 'orange',
    quote:
      'Standard Accounting have been my Accounting as well as Audit firm since 2015, and they have been, and are still providing par excellence service to Idea Tree Advertising LLC. The people are extremely professional and pleasant to deal with.',
    initials: 'CK',
    avatarTone: 'navy',
    name: 'Cherian Kuruvilla',
    company: 'Idea Tree Advertising LLC',
    featured: true,
  },
  {
    tag: 'Long-standing partner',
    tagTone: 'navy',
    quote:
      'Standard Auditing performs quality audits and maintains the highest standards of professionalism. Their presence for more than a decade is a testimony of their efficiency and quality of audit.',
    initials: 'MP',
    avatarTone: 'orange',
    name: 'Mayur Prabhu',
    company: 'T.D. Williamson',
  },
  {
    tag: 'Managing Director',
    tagTone: 'navy',
    quote:
      'We have maintained a longstanding partnership with the esteemed team at Standard Auditing, which has greatly contributed to our success journey. Attesting to the exceptional quality of their services and value they have brought to our business.',
    initials: 'SK',
    avatarTone: 'navy',
    name: 'Senthil Kumar',
    company: 'Triway Technologies LLC',
  },
  {
    tag: 'Financial Manager',
    tagTone: 'navy',
    quote:
      'We have been working with Standard Auditing since 2016 for our auditing, accounts, and taxation needs. They have streamlined our financial processes and proven to be both proficient and trustworthy.',
    initials: 'R',
    avatarTone: 'orange',
    name: 'Ravichandran',
    company: 'Silver Bright',
  },
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCardsPerView(1);
      } else if (window.innerWidth <= 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalCards = testimonialsData.length;
  const maxIndex = Math.max(0, totalCards - cardsPerView);

  // Keep index in valid range if resized
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [cardsPerView, maxIndex, currentIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  // Touch swipe support for mobile
  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(null);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    const minSwipeDistance = 45; // px

    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  // Sliding translation (gap matches CSS: 24px desktop, 20px below 1024px)
  let transformValue = 'translateX(0px)';
  if (cardsPerView === 3) {
    transformValue = `translateX(calc(-${currentIndex} * ((100% - 48px) / 3 + 24px)))`;
  } else if (cardsPerView === 2) {
    transformValue = `translateX(calc(-${currentIndex} * ((100% - 20px) / 2 + 20px)))`;
  } else if (cardsPerView === 1) {
    transformValue = `translateX(calc(-${currentIndex} * (100% + 20px)))`;
  }

  // Dynamic progress track indicator
  const progressTrackWidth = 80; // px
  const indicatorWidth = maxIndex > 0 ? progressTrackWidth / (maxIndex + 1) : progressTrackWidth;
  const indicatorLeft = maxIndex > 0 ? (currentIndex / maxIndex) * (progressTrackWidth - indicatorWidth) : 0;

  return (
    <section className="bk-testimonials" id="testimonials" data-screen-label="Testimonials">
      <div className="bk-container">
        <header className="bk-testimonials-head">
          <div>
            <span className="bk-pill bk-pill--peach">
              <span className="bk-dot" aria-hidden="true" />
              Client spotlight
            </span>
            <h2 className="bk-h2 bk-h2--xl">
              What Dubai business owners say <span className="bk-accent">after month one.</span>
            </h2>
          </div>
        </header>

        <div
          className="bk-testimonials-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="bk-testimonials-track" style={{ transform: transformValue }}>
            {testimonialsData.map((item, index) => (
              <figure
                key={item.name}
                className={`bk-testimonial ${item.featured ? 'is-featured' : ''}`}
                aria-hidden={index < currentIndex || index >= currentIndex + cardsPerView}
              >
                <span className={`bk-testimonial-tag bk-testimonial-tag--${item.tagTone}`}>{item.tag}</span>
                <div className="bk-testimonial-top">
                  <span className="bk-stars" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((s) => <IconStar key={s} size={14} />)}
                  </span>
                </div>
                <blockquote className="bk-testimonial-quote">“{item.quote}”</blockquote>
                <figcaption className="bk-testimonial-author">
                  <span className={`bk-avatar bk-avatar--${item.avatarTone}`} aria-hidden="true">{item.initials}</span>
                  <span>
                    <span className="bk-testimonial-name">{item.name}</span>
                    <span className="bk-testimonial-role">@ {item.company}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        {maxIndex > 0 && (
          <div className="bk-testimonials-controls">
            <div className="bk-progress-track" aria-hidden="true">
              <div
                className="bk-progress-bar"
                style={{
                  width: `${indicatorWidth}px`,
                  transform: `translateX(${indicatorLeft}px)`,
                }}
              />
            </div>

            <div className="bk-arrow-btns">
              <button type="button" onClick={handlePrev} className="bk-arrow-btn" aria-label="Previous testimonials">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
              <button type="button" onClick={handleNext} className="bk-arrow-btn" aria-label="Next testimonials">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
